import { Collapse } from 'antd';
import { useField } from 'formik';
import { useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useListFinanceProductsQuery } from '../../../../api/queries/listFinanceProducts';
import { Purpose } from '../../../../api/types';
import TransferField from '../../../../components/fields/TransferField';
import CollapsibleWrapper from '../../../../components/wrappers/CollapsibleWrapper';

const { Panel } = Collapse;

export type FinanceProductAssignmentSectionProps = {
    moduleId: string;
    variantSuiteIds?: string[] | null;
    disabled?: boolean;
};

type RecordType = {
    key: string;
    title: string;
};

const filterOption = (inputValue: string, option: RecordType) =>
    option.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;

const FinanceProductAssignmentSection = ({
    moduleId,
    variantSuiteIds = null,
    disabled,
}: FinanceProductAssignmentSectionProps) => {
    const { t } = useTranslation(['dealershipManagement']);

    const [{ value: currentValue }, , { setValue }] = useField('dealerFinanceProductSuiteIds');

    // fetch finance products
    const { data: localFinanceProductData } = useListFinanceProductsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter: { moduleId, purpose: Purpose.Admin } },
    });

    const financeProductDataSource: RecordType[] = useMemo(() => {
        if (!localFinanceProductData) {
            return [];
        }

        if (variantSuiteIds) {
            return localFinanceProductData.list.items
                .map(fp => {
                    if (fp.allVariantSuiteIds.some(id => variantSuiteIds.includes(id))) {
                        return {
                            key: fp.versioning.suiteId,
                            title: fp.displayName,
                        };
                    }

                    return null;
                })
                .filter(data => data !== null);
        }

        return localFinanceProductData.list.items.map(fp => ({
            key: fp.versioning.suiteId,
            title: fp.displayName,
        }));
    }, [localFinanceProductData, variantSuiteIds]);

    const shouldAutoCleanValues = !!variantSuiteIds && localFinanceProductData;

    useLayoutEffect(() => {
        if (shouldAutoCleanValues) {
            const financeProductSuiteIds = financeProductDataSource.map(data => data.key);
            const nextValue = currentValue.filter(element => financeProductSuiteIds.indexOf(element) !== -1);

            if (nextValue.length !== currentValue.length) {
                setValue(nextValue);
            }
        }
    }, [shouldAutoCleanValues, financeProductDataSource, setValue, currentValue]);

    return (
        <CollapsibleWrapper defaultActiveKey={['assignVehicles']}>
            <Panel key="assignVehicles" header={t('dealershipManagement:tabs.panel.financeProductAssignment')}>
                <TransferField
                    dataSource={financeProductDataSource}
                    disabled={disabled}
                    filterOption={filterOption}
                    listStyle={{ width: '100%' }}
                    name="dealerFinanceProductSuiteIds"
                    render={item => item.title}
                />
            </Panel>
        </CollapsibleWrapper>
    );
};

export default FinanceProductAssignmentSection;
